import { CaretRight, PencilSimple, DotsThree } from "@phosphor-icons/react";
import { Flex, Link, DropdownMenu, IconButton } from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { Link as NavLink, useRouter } from "@tanstack/react-router";
import cx from "classnames";
import { Fragment, type ReactNode, useState, useRef, useEffect } from "react";

import { FolderInlineEdit } from "./FolderInlineEdit";
import { TicketActionsDropdown } from "./TicketActionsDropdown";
import { TicketInlineEdit } from "./TicketInlineEdit";

import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../../../components/sidebar/SidebarTooltip";
import { isPunttGuest } from "../../../utils/auth";
import { useAppSelector } from "../../../utils/hooks";
import { useUpdateFolderMutation } from "../../../utils/queries/projects";
import { ticketsRoute } from "../route";

type BreadcrumbsProps = {
  onDeleteFolder(): void;
  onRenameFolder(): void;
  onShareFolder(): void;
  onDownloadFolder(): void;
  onManageVersions?(): void;
  ticketTitle?: ReactNode;
  ticketId?: string;
  onRenameTicket?: (ticketId: string, title: string) => void;
  onDeleteTicket?: (ticketId: string) => void;
  onShareTicket?: (ticketId: string) => void;
  onDownloadTicket?: (ticketId: string) => void;
  onMoveToFolder?: (
    draggedIds: { _id: string; isFolder: boolean }[],
    targetFolderId: string,
  ) => void;
};

export function TruncateWithTooltip({ children }: { children: string }) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  const prevChildrenRef = useRef(children);

  useEffect(() => {
    const el = spanRef.current;
    if (el && prevChildrenRef.current !== children) {
      // Use requestAnimationFrame for better performance
      const frameId = requestAnimationFrame(() => {
        try {
          if (el && el.isConnected) {
            setTruncated(el.scrollWidth > el.clientWidth);
          }
        } catch (error) {
          console.error("Error checking truncation:", error);
          setTruncated(false);
        }
      });

      prevChildrenRef.current = children;

      return () => cancelAnimationFrame(frameId);
    }
  }, [children]);

  return truncated ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span ref={spanRef} className="block w-full truncate">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" align="center">
        {children}
      </TooltipContent>
    </Tooltip>
  ) : (
    <span ref={spanRef} className="block w-full truncate">
      {children}
    </span>
  );
}

export function Breadcrumbs(props: BreadcrumbsProps) {
  const router = useRouter();
  const {
    onRenameFolder,
    onShareFolder,
    onDeleteFolder,
    onDownloadFolder,
    onManageVersions,
    ticketTitle,
    ticketId,
    onRenameTicket,
    onDeleteTicket,
    onShareTicket,
    onDownloadTicket,
    onMoveToFolder,
  } = props;
  const breadcrumbs = useAppSelector(
    (state) => state.punttProjects.breadcrumbs,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isEditingTicket, setIsEditingTicket] = useState(false);
  const [ticketHovered, setTicketHovered] = useState(false);
  const [isOver, setIsOver] = useState(false);
  const updateFolderMutation = useUpdateFolderMutation();

  // If we have breadcrumbs, use them; otherwise, we're in a first-level folder
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const init = hasBreadcrumbs ? breadcrumbs.slice(0, -1) : [];
  const last = hasBreadcrumbs ? breadcrumbs[breadcrumbs.length - 1] : null;

  // Check if we're on the root page (no breadcrumbs, no ticket title)
  const isOnRootPage = !hasBreadcrumbs && !ticketTitle;

  // Always show Projects link in all views
  const shouldShowProjectsLink = true;

  // Breadcrumb truncation logic - show max 3 items total before collapsing
  // When truncating: Projects > ... > parent folder > current folder
  const totalItems = 1 + init.length + (last ? 1 : 0) + (ticketTitle ? 1 : 0); // Projects + folders + current + ticket
  const shouldTruncate = totalItems > 3;

  let visibleBreadcrumbs: typeof init = [];
  let hiddenBreadcrumbs: typeof init = [];

  if (shouldTruncate) {
    // When truncating, show: Projects > ... > parent folder > current folder
    if (ticketTitle) {
      // When viewing a ticket: Projects > ... > current folder > ticket name
      // Show the current folder (last from breadcrumbs) as visible, parent folders in dropdown
      if (breadcrumbs && breadcrumbs.length >= 2) {
        // Don't show current folder in visible breadcrumbs (it will be shown as last)
        visibleBreadcrumbs = [];
        // Hide all parent folders (everything except the current folder)
        hiddenBreadcrumbs = breadcrumbs.slice(0, -1);
      } else if (breadcrumbs && breadcrumbs.length === 1) {
        // Only one folder, don't show it in visible breadcrumbs (it will be shown as last)
        visibleBreadcrumbs = [];
        hiddenBreadcrumbs = [];
      } else {
        visibleBreadcrumbs = [];
        hiddenBreadcrumbs = [];
      }
    } else {
      // When viewing a folder: Projects > ... > parent folder > current folder
      // Show the parent folder (second to last from breadcrumbs) as visible, current folder as last
      if (breadcrumbs && breadcrumbs.length >= 2) {
        // Show the parent folder (second to last) as visible breadcrumb
        visibleBreadcrumbs = [breadcrumbs[breadcrumbs.length - 2]];
        // Hide all folders except the parent and current
        hiddenBreadcrumbs = breadcrumbs.slice(0, -2);
      } else if (breadcrumbs && breadcrumbs.length === 1) {
        // Only one folder, show it as last (not in visible breadcrumbs)
        visibleBreadcrumbs = [];
        hiddenBreadcrumbs = [];
      } else {
        visibleBreadcrumbs = [];
        hiddenBreadcrumbs = [];
      }
    }
  } else {
    // No truncation needed, show all folders except the last one
    visibleBreadcrumbs = init;
    hiddenBreadcrumbs = [];
  }

  function renderDropdownActions() {
    if (isPunttGuest()) {
      return null;
    }

    // If we have a ticket, show ticket actions
    if (ticketId && onDeleteTicket && onShareTicket && onDownloadTicket) {
      return (
        <TicketActionsDropdown
          onShare={() => onShareTicket(ticketId)}
          onDeleteTicket={() => onDeleteTicket(ticketId)}
          onRename={() => {
            if (onRenameTicket && typeof ticketTitle === "string") {
              setIsEditingTicket(true);
            }
          }}
          onDownload={() => onDownloadTicket(ticketId)}
          onManageVersions={onManageVersions}
        />
      );
    }

    // Otherwise show folder actions
    return (
      <TicketActionsDropdown
        onShare={onShareFolder}
        onDeleteTicket={onDeleteFolder}
        onRename={onRenameFolder}
        onDownload={onDownloadFolder}
        onManageVersions={onManageVersions}
      />
    );
  }
  return (
    <Flex
      gap="2"
      align="center"
      className="h-10 min-w-0 flex-1 overflow-hidden"
      data-testid="breadcrumbs-container"
    >
      {/* Always show Projects link in all views */}
      {shouldShowProjectsLink && (
        <Link
          asChild
          className={cx("puntt-link", {
            "rounded-lg bg-puntt-accent-10 text-base-white outline outline-1 outline-offset-2 outline-puntt-accent-8":
              isOver,
          })}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setIsOver(true);
          }}
          onDragLeave={() => {
            setIsOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsOver(false);

            try {
              const parsed = JSON.parse(
                e.dataTransfer.getData("application/json"),
              );

              if (
                Array.isArray(parsed) &&
                parsed.every(
                  (item) =>
                    typeof item === "object" &&
                    item !== null &&
                    "_id" in item &&
                    typeof item._id === "string" &&
                    "isFolder" in item &&
                    typeof item.isFolder === "boolean",
                ) &&
                typeof onMoveToFolder === "function"
              ) {
                onMoveToFolder(
                  parsed as { _id: string; isFolder: boolean }[],
                  "",
                );
              }
            } catch (err) {
              console.error("Invalid drag data", err);
            }
          }}
        >
          <NavLink
            to={ticketsRoute.to}
            search={({ folderId: _folderId, ...prev }) => prev}
            className="puntt-link text-[20px] font-bold leading-[28px] tracking-[-0.08px]"
            data-testid="breadcrumb-projects-link"
            onContextMenu={(e) => {
              e.preventDefault();
              const url = router.buildLocation({
                to: ticketsRoute.to,
                search: { folderId: undefined },
              });
              window.open(url.href, "_blank");
            }}
          >
            Documents
          </NavLink>
        </Link>
      )}
      {/* Show "..." dropdown when truncating */}
      {shouldTruncate && hiddenBreadcrumbs.length > 0 && (
        <>
          <CaretRight className="shrink-0" />
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                data-testid="breadcrumb-truncate-dropdown-trigger"
              >
                <DotsThree
                  size={16}
                  weight="bold"
                  className="text-on-surface"
                />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content data-testid="breadcrumb-truncate-dropdown-content">
              {hiddenBreadcrumbs.map((folder) => (
                <DropdownMenu.Item
                  key={folder._id}
                  className="text-on-surface radix-highlight:bg-accent radix-highlight:text-on-accent cursor-pointer"
                  asChild
                  data-testid={`breadcrumb-truncate-item-${folder._id}`}
                >
                  <NavLink
                    to={ticketsRoute.to}
                    search={(prev) => ({ ...prev, folderId: folder._id })}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      const url = router.buildLocation({
                        to: ticketsRoute.to,
                        search: { folderId: folder._id },
                      });
                      window.open(url.href, "_blank");
                    }}
                  >
                    <TruncateWithTooltip>{folder.name}</TruncateWithTooltip>
                  </NavLink>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </>
      )}
      {visibleBreadcrumbs.map((b, _idx) => (
        <Fragment key={b._id}>
          <CaretRight className="shrink-0" />
          <BreadcrumbLink
            folder={b}
            onMoveToFolder={onMoveToFolder}
            router={router}
          />
        </Fragment>
      ))}
      {last && (
        <>
          <CaretRight className="shrink-0" />
          {ticketTitle ? (
            // When viewing a ticket, the last folder should be a link, not editable
            <BreadcrumbLink
              folder={last}
              onMoveToFolder={onMoveToFolder}
              router={router}
            />
          ) : (
            // When viewing a folder, the last folder should be editable
            <div
              className="relative flex min-w-0 items-center overflow-hidden"
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
            >
              {isEditing ? (
                <FolderInlineEdit
                  folderId={last._id}
                  folderName={last.name}
                  onSave={async (name) => {
                    await updateFolderMutation.mutateAsync({
                      _id: last._id,
                      name,
                    });
                  }}
                  isPending={updateFolderMutation.isPending}
                  onCancel={() => {
                    setIsEditing(false);
                  }}
                />
              ) : (
                <>
                  <button
                    type="button"
                    className="text-black min-w-0 overflow-hidden border-none bg-transparent p-0 text-left text-[20px] leading-[28px] tracking-[-0.08px]"
                    onClick={(e) => {
                      if (hovered) {
                        e.preventDefault();
                        setIsEditing(true);
                      }
                    }}
                    style={{ cursor: hovered ? "pointer" : undefined }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && hovered) {
                        e.preventDefault();
                        setIsEditing(true);
                      }
                    }}
                    data-testid={`breadcrumb-link-${last._id}`}
                  >
                    <TruncateWithTooltip>{last.name}</TruncateWithTooltip>
                  </button>
                  {hovered && !isPunttGuest() && (
                    <button
                      className="hover:bg-gray-100 cursor-pointer rounded border-none bg-transparent p-1"
                      aria-label="Edit folder name"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsEditing(true);
                      }}
                      data-testid={`breadcrumb-edit-pencil-${last._id}`}
                    >
                      <PencilSimple size={18} className="text-gray-600" />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
      {ticketTitle && (
        <>
          <CaretRight className="shrink-0" />
          <div
            className="relative flex min-w-0 items-center overflow-hidden"
            onMouseEnter={() => setTicketHovered(true)}
            onMouseLeave={() => setTicketHovered(false)}
          >
            {isEditingTicket && typeof ticketTitle === "string" ? (
              <TicketInlineEdit
                ticketId={ticketId}
                ticketTitle={ticketTitle}
                onSave={async (title) => {
                  if (!ticketId || !onRenameTicket) {
                    throw new Error("Missing ticket ID or rename function");
                  }
                  await onRenameTicket(ticketId, title);
                }}
                onCancel={() => {
                  setIsEditingTicket(false);
                }}
              />
            ) : (
              <>
                <button
                  type="button"
                  className="min-w-0 overflow-hidden border-none bg-transparent p-0 text-left text-[20px] font-normal leading-[28px] tracking-[-0.08px]"
                  onClick={(e) => {
                    if (
                      ticketHovered &&
                      ticketId &&
                      onRenameTicket &&
                      typeof ticketTitle === "string"
                    ) {
                      e.preventDefault();
                      setIsEditingTicket(true);
                    }
                  }}
                  style={{ cursor: ticketHovered ? "pointer" : undefined }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      ticketHovered &&
                      ticketId &&
                      onRenameTicket &&
                      typeof ticketTitle === "string"
                    ) {
                      e.preventDefault();
                      setIsEditingTicket(true);
                    }
                  }}
                  data-testid={`breadcrumb-ticket-link-${ticketId}`}
                >
                  <TruncateWithTooltip>
                    {typeof ticketTitle === "string" ? ticketTitle : ""}
                  </TruncateWithTooltip>
                </button>
                {ticketHovered && ticketId && onRenameTicket && (
                  <button
                    className="hover:bg-gray-100 cursor-pointer rounded border-none bg-transparent p-1"
                    aria-label="Edit ticket name"
                    onClick={(e) => {
                      e.preventDefault();
                      if (typeof ticketTitle === "string") {
                        setIsEditingTicket(true);
                      }
                    }}
                    data-testid={`breadcrumb-ticket-edit-pencil-${ticketId}`}
                  >
                    <PencilSimple size={18} className="text-gray-600" />
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
      <div
        className="flex items-center"
        style={{ marginRight: 4 }}
        data-testid="breadcrumb-actions-container"
      >
        {!isEditing &&
          !hovered &&
          !isEditingTicket &&
          !ticketHovered &&
          !isOnRootPage &&
          renderDropdownActions()}
      </div>
    </Flex>
  );
}

function BreadcrumbLink({
  folder,
  onMoveToFolder,
  router,
}: {
  folder: { _id: string; name: string };
  onMoveToFolder?: (
    draggedIds: { _id: string; isFolder: boolean }[],
    targetFolderId: string,
  ) => void;
  router: any;
}) {
  const [isOver, setIsOver] = useState(false);

  return (
    <Link
      asChild
      className={cx("puntt-link min-w-0 overflow-hidden", {
        "rounded-lg bg-puntt-accent-10 text-base-white outline outline-1 outline-offset-2 outline-puntt-accent-8":
          isOver,
      })}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setIsOver(true);
      }}
      onDragLeave={() => {
        setIsOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);

        try {
          const parsed = JSON.parse(e.dataTransfer.getData("application/json"));

          if (
            Array.isArray(parsed) &&
            parsed.every(
              (item) =>
                typeof item === "object" &&
                item !== null &&
                "_id" in item &&
                typeof item._id === "string" &&
                "isFolder" in item &&
                typeof item.isFolder === "boolean",
            )
          ) {
            // Only proceed with the move if we're not dropping onto the same folder
            const typedParsed = parsed as { _id: string; isFolder: boolean }[];
            const isDropOnSelf = typedParsed.some(
              (item) => item._id === folder._id,
            );
            if (!isDropOnSelf && typeof onMoveToFolder === "function") {
              onMoveToFolder(typedParsed, folder._id);
            }
          }
        } catch (err) {
          console.error("Invalid drag data", err);
        }
      }}
    >
      <NavLink
        to={ticketsRoute.to}
        search={(prev) => ({ ...prev, folderId: folder._id })}
        className="puntt-link min-w-0 overflow-hidden text-[20px] leading-[28px] tracking-[-0.08px]"
        data-testid={`breadcrumb-link-${folder._id}`}
        onContextMenu={(e) => {
          e.preventDefault();
          const url = router.buildLocation({
            to: ticketsRoute.to,
            search: { folderId: folder._id },
          });
          window.open(url.href, "_blank");
        }}
      >
        <TruncateWithTooltip>{folder.name}</TruncateWithTooltip>
      </NavLink>
    </Link>
  );
}
